import React, { useContext, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FaSignOutAlt, FaChevronDown } from "react-icons/fa";
import { Auth } from "../../rbac/rbac";
import { ROLE_LABELS } from "../../utils/constants";
import "./user-menu.css";

/** Show internal community/division ids only outside of production, to aid debugging */
const SHOW_IDS = process.env.REACT_APP_ENV === "development";

/** Builds the upper-cased initials for the avatar placeholder */
const getInitials = (firstName, lastName) =>
  `${(firstName || "").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase();

/**
 * Renders the logged in user's image if it exists, otherwise a placeholder
 * containing their initials. Falls back to the initials if the image fails to load.
 */
const Avatar = ({ imageUrl, initials, muted }) => {
  const [errored, setErrored] = useState(false);

  if (imageUrl && !errored) {
    return (
      <img
        className="user-menu-avatar"
        src={imageUrl}
        alt={initials}
        onError={() => setErrored(true)}
      />
    );
  }

  return (
    <div
      className={`user-menu-avatar user-menu-avatar--initials${
        muted ? " user-menu-avatar--muted" : ""
      }`}
    >
      {initials}
    </div>
  );
};

/**
 * Custom toggle for the user menu dropdown. Renders the user's avatar and
 * forwards the click/ref that react-bootstrap needs to open the menu.
 */
const UserMenuToggle = React.forwardRef(({ imageUrl, initials, muted, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="user-menu-toggle"
    aria-label="User menu"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <Avatar imageUrl={imageUrl} initials={initials} muted={muted} />
    <FaChevronDown className="user-menu-caret" />
  </button>
));

/** A labelled section inside the dropdown that lists a set of values */
const MenuSection = ({ title, children }) => (
  <div className="user-menu-section">
    <div className="user-menu-section-title">{title}</div>
    {children}
  </div>
);

/**
 * Dropdown menu shown in the top right of the application. It lets the logged in
 * user see their name, role(s), division(s) and community/communities (inactive
 * communities are flagged) and provides the logout action.
 */
const UserMenu = () => {
  const { user, signout, is_logged_in_as } = useContext(Auth);
  const loggedInAs = is_logged_in_as();

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const initials = getInitials(user.first_name, user.last_name);

  // dedupe so a user whose primary and secondary role match shows a single entry
  const roles = [...new Set([user.role, user.secondary_role].filter(Boolean))]
    .map((role) => ROLE_LABELS[role] || role);

  const divisions = (user.Divisions || []).map((division) =>
    SHOW_IDS ? `${division.name} (${division.id})` : division.name
  );

  const communities = [...(user.Communities || [])].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
  );

  return (
    <Dropdown alignRight className="user-menu">
      <Dropdown.Toggle
        as={UserMenuToggle}
        imageUrl={user.image_url}
        initials={initials}
        muted={loggedInAs}
      />

      <Dropdown.Menu className="user-menu-dropdown">
        <div className="user-menu-header">
          <Avatar imageUrl={user.image_url} initials={initials} />
          <div className="user-menu-header-info">
            <span className="user-menu-name">{fullName}</span>
            {user.email_address && (
              <span className="user-menu-email">{user.email_address}</span>
            )}
          </div>
        </div>

        <Dropdown.Divider />

        {roles.length > 0 && (
          <MenuSection title={roles.length > 1 ? "Roles" : "Role"}>
            {roles.map((role) => (
              <div key={role} className="user-menu-item">
                {role}
              </div>
            ))}
          </MenuSection>
        )}

        {divisions.length > 0 && (
          <MenuSection title={divisions.length > 1 ? "Divisions" : "Division"}>
            {divisions.map((division) => (
              <div key={division} className="user-menu-item">
                {division}
              </div>
            ))}
          </MenuSection>
        )}

        {communities.length > 0 && (
          <MenuSection
            title={communities.length > 1 ? "Communities" : "Community"}
          >
            <div className="user-menu-communities">
              {communities.map((community) => (
                <div key={community.id} className="user-menu-item">
                  <span className="user-menu-community-name">
                    {community.name}
                    {SHOW_IDS ? ` (${community.id})` : ""}
                  </span>
                  {!community.is_active && (
                    <span className="user-menu-inactive-badge">Inactive</span>
                  )}
                </div>
              ))}
            </div>
          </MenuSection>
        )}

        {/* Logout is hidden while impersonating - exit is handled by the View As control */}
        {!loggedInAs && (
          <>
            <Dropdown.Divider />
            <button
              type="button"
              className="user-menu-logout"
              onClick={() => signout()}
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default UserMenu;
